/**
 * Analytics bridge. Fires each event to every analytics platform present on the
 * page — GA4 (gtag), Google Tag Manager (dataLayer) and Vercel Analytics (va) —
 * so wiring a new platform only means adding it here.
 */

export type AnalyticsEvent =
  | 'hero_book_demo_click'
  | 'hero_video_play'
  | 'video_progress_25'
  | 'video_progress_50'
  | 'video_progress_75'
  | 'video_progress_100'
  | 'moat_cta_click'
  | 'deal_radar_cta_click'
  | 'security_cta_click'
  | 'final_book_demo_click'
  | 'nav_book_demo_click'
  | 'mobile_sticky_book_demo_click'
  | 'section_book_demo_click'

type AnalyticsWindow = Window & {
  gtag?: (...args: unknown[]) => void
  dataLayer?: unknown[]
  va?: (event: 'event', payload: { name: string; data?: Record<string, unknown> }) => void
}

/** Google Analytics 4 measurement ID. */
export const GA_MEASUREMENT_ID = 'G-JM9VK2QN0M'

/**
 * Installs the Google tag (gtag.js) — equivalent to the standard snippet in
 * <head>. Runs once at startup, before the app renders.
 */
export function initGoogleAnalytics() {
  if (typeof window === 'undefined') return
  const w = window as AnalyticsWindow
  if (document.querySelector(`script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`)) return

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  w.dataLayer = w.dataLayer || []
  w.gtag = function gtag() {
    // gtag.js requires the native `arguments` object, not a spread array.
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments)
  }
  w.gtag('js', new Date())
  w.gtag('config', GA_MEASUREMENT_ID)
}

export function trackEvent(name: AnalyticsEvent, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined') return
  const w = window as AnalyticsWindow
  const payload = { ...params, page_path: window.location.pathname }

  try {
    w.gtag?.('event', name, payload)
    // GTM-style push for any tag-manager triggers (ignored by GA4 itself).
    w.dataLayer?.push({ event: name, ...payload })
    w.va?.('event', { name, data: payload })
  } catch {
    // Analytics must never break the page.
  }
}
